/** Small date helpers for the version UI — friendly relative ages plus an exact label. */

/** "today" / "yesterday" / "N days ago" / "N weeks ago", falling back to an exact date. */
export function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const days = Math.floor((Date.now() - then) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 14) return 'last week';
  if (days < 60) return `${Math.floor(days / 7)} weeks ago`;
  return formatDate(iso);
}

/** Exact calendar date, e.g. "Jun 28, 2026". */
export function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {month: 'short', day: 'numeric', year: 'numeric'});
}

/** Exact date + time, e.g. "Jun 28, 2026, 4:30 PM". */
export function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit'});
}

/**
 * A JTBD's lastUpdated as a fixed stamp, e.g. "Updated 6/30/26". A fixed date doesn't drift
 * day-to-day the way a relative "Xd ago" does, and reads as a fact rather than an alert. Parses the
 * date portion directly (no Date() timezone shift). Falls back to the raw value if it can't parse.
 * Ported from the prototype (local/chris-v2/docs/index.html).
 */
export function updatedStamp(dateStr?: string): string {
  if (!dateStr) return 'No date';
  const datePart = String(dateStr).split('T')[0];
  const [y, m, d] = datePart.split('-').map(Number);
  if (!y || !m || !d) return `Updated ${dateStr}`;
  return `Updated ${m}/${d}/${String(y).slice(2)}`;
}

/**
 * Formats an ISO timestamp for display. Shows date only when time is midnight/absent (today's data is
 * date-only, written as T00:00:00Z); shows date + time once real time-of-day values start flowing in
 * from the FROM-TO Baseline Skill. Ported from the prototype.
 */
export function formatLastUpdated(iso?: string): string | null {
  if (!iso) return null;
  const [datePart, timePartRaw] = String(iso).split('T');
  const [y, m, d] = datePart.split('-').map(Number);
  if (!y || !m || !d) return String(iso).slice(0, 10);
  const dateObj = new Date(y, m - 1, d);
  const dateStr = dateObj.toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'});
  const timePart = (timePartRaw || '').replace('Z', '');
  const [hh, mm] = timePart.split(':').map(Number);
  const hasRealTime = !(hh === 0 && (mm === 0 || mm === undefined));
  if (!hasRealTime) return dateStr;
  const timeObj = new Date(y, m - 1, d, hh, mm);
  const timeStr = timeObj.toLocaleTimeString('en-US', {hour: 'numeric', minute: '2-digit'});
  return `${dateStr} · ${timeStr}`;
}
